import React, { useEffect, useRef, useState } from "react";
import { Container, Section, PageHero, Card, Eyebrow, CTABand } from "@/components/ui-bits/Primitives";
import { motion, useInView, useMotionValue, useTransform, animate } from "framer-motion";
import { Leaf, Sun, BatteryCharging, Users } from "lucide-react";
import { IMAGES } from "@/lib/site-data";
import { fetchImpactMetrics } from "@/lib/api";

function BigCounter({ to, suffix = "", duration = 2.4 }) {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true });
    const mv = useMotionValue(0);
    const rounded = useTransform(mv, (v) => Math.floor(v).toLocaleString());
    useEffect(() => { if (inView) animate(mv, to, { duration, ease: "easeOut" }); }, [inView, to, mv, duration]);
    return <span ref={ref}><motion.span>{rounded}</motion.span>{suffix}</span>;
}

export default function Sustainability() {
    const [metrics, setMetrics] = useState({
        kwh_delivered: 184000, co2_offset_lbs: 268000, charging_sessions: 12400,
        active_stations: 8, communities_served: 2, renewable_pct: 42,
    });

    useEffect(() => {
        fetchImpactMetrics().then(setMetrics).catch(() => {});
    }, []);

    const counters = [
        { v: metrics.kwh_delivered, s: "+", l: "kWh Delivered", icon: BatteryCharging },
        { v: metrics.co2_offset_lbs, s: " lbs", l: "CO₂ Emissions Offset", icon: Leaf },
        { v: metrics.charging_sessions, s: "+", l: "Charging Sessions", icon: BatteryCharging },
        { v: metrics.active_stations, s: "", l: "Active & Planned Stations", icon: Sun },
        { v: metrics.communities_served, s: "", l: "Communities Served", icon: Users },
        { v: metrics.renewable_pct, s: "%", l: "Renewable Energy Integration", icon: Sun },
    ];

    return (
        <div data-testid="sustainability-page">
            <PageHero
                kicker="Sustainability"
                title="Impact measured in kilowatts and communities."
                lead="Every charge displaces fossil fuels, lowers operating costs, and brings new economic opportunity to the neighborhoods we serve."
                image={IMAGES.solarCanopy}
            />

            <Section>
                <Container>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {counters.map((c) => {
                            const I = c.icon;
                            return (
                                <Card key={c.l} hover={false} className="!p-8">
                                    <I className="w-7 h-7 text-electric-400 mb-6" strokeWidth={1.5} />
                                    <div className="font-heading font-medium text-4xl md:text-5xl text-electric-400 tracking-tight">
                                        <BigCounter to={c.v} suffix={c.s} />
                                    </div>
                                    <div className="mt-3 text-[11px] uppercase tracking-[0.2em] text-slate-400">{c.l}</div>
                                </Card>
                            );
                        })}
                    </div>

                    <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                        <div>
                            <Eyebrow className="mb-4">Community Impact</Eyebrow>
                            <h2 className="font-heading text-3xl md:text-4xl text-white tracking-tight leading-tight">Equity by design, not by accident.</h2>
                            <p className="mt-6 text-slate-400 leading-relaxed">We deliberately prioritize rural Texas, underserved urban communities, and infrastructure deserts where access to charging has lagged. Every site we develop carries an ADA-compliant design baseline and equity-focused siting metrics.</p>
                        </div>
                        <div className="relative overflow-hidden aspect-[16/10]">
                            <img src={IMAGES.solarCanopy} alt="Solar canopy" className="w-full h-full object-cover" />
                            <div className="absolute inset-0 bg-gradient-to-tr from-ink-900/70 via-transparent to-transparent" />
                        </div>
                    </div>
                </Container>
            </Section>

            <CTABand title="Help us electrify the next community." subtitle="Site host applications, fleet partnerships, and municipal P3s welcome." />
        </div>
    );
}
